import { Button } from '~/shadcn/ui/button/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '~/shadcn/ui/dialog'
import { Spinner } from '~/shadcn/ui/spinner'
import { FieldError } from '~/shadcn/ui/field'
import Analytics from '~/@lib_integrations/analytics/analytics'
import { TwoFaVerifyForm } from '~/@mid_2fa/twofa-verify-form/TwoFaVerifyForm'
import { useUser } from '~/@biz_account/useUser/useUser'
import { useEmailVerification } from './useEmailVerification'
import { HeadingTwo } from '~/@mid_ui/typography/HeadingTwo'
import { useLocalNotifications } from '~/@mid_ui/local-notifications/useLocalNotifications'
import { DevWidgetWrapper } from '~/@dev-buttons/dev-widget-wrapper/DevWidgetWrapper'
import { DevEmailVerification } from '~/@dev-buttons/email-verification/DevEmailVerification'

type Props = {
  isDialogOpen: boolean
  handleCloseDialog: () => void
}

function EmailVerificationDialog({ isDialogOpen, handleCloseDialog }: Props) {
  const { setNotification } = useLocalNotifications()
  const { reloadUser } = useUser()
  const {
    verifyEmail,
    isRequestingVerifyEmail,
    verifyEmailError,
    resendEmail,
    isRequestingResendEmail,
    resendEmailError,
  } = useEmailVerification(reloadUser)

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={handleCloseDialog}>
        <DialogContent>
          <TwoFaVerifyForm
            codeLength={4}
            formContent={
              <div className="flex flex-col items-center gap-2 text-center">
                <DialogTitle asChild>
                  <HeadingTwo className="text-2xl max-w-md py-8">
                    Overte svoju e-mailovú adresu
                  </HeadingTwo>
                </DialogTitle>
                <DialogDescription asChild>
                  <p className="text-md max-w-md">
                    Na vami zadaný email sme zaslali overovací kód. Skontrolujte
                    si prosím aj priečinky spam a reklama.
                  </p>
                </DialogDescription>
              </div>
            }
            isDisabled={isRequestingResendEmail}
            fieldLabel="Alebo zadajte overovací kód"
            FooterComponent={(props) => (
              <DialogFooter
                className="flex flex-col justify-center sm:justify-center lg:flex-row mt-8 gap-4 w-full"
                {...props}
              />
            )}
            submitButtonLabel="Overiť e-mail"
            handleSubmitForm={({ code }) =>
              verifyEmail(code, {
                onSuccess: () => {
                  Analytics.track('user-verified-email')
                  setNotification(
                    'success',
                    'E-mailová adresa bola úspešne overená.'
                  )
                },
              })
            }
            isRequesting={isRequestingVerifyEmail}
            error={verifyEmailError}
          />

          <div className="text-center text-sm text-gray-500">
            Neprišiel Vám overovací e-mail?{' '}
            <Button
              variant="link"
              onClick={() => resendEmail()}
              disabled={isRequestingResendEmail}
              className="hover:no-underline text-accent">
              Zaslať e-mail znova{` `}
              {isRequestingResendEmail && <Spinner className="size-6" />}
            </Button>
            {resendEmailError && <FieldError>{resendEmailError}</FieldError>}
          </div>

          {/* ONLY FOR DEV MODE */}
          {import.meta.env.VITE_HIDE_DEV !== 'true' && (
            <DevWidgetWrapper>
              <DevEmailVerification />
            </DevWidgetWrapper>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

export { EmailVerificationDialog }
